<template>
  <div>
    <v-dialog v-model="showImport" persistent>
      <v-card>
        <v-card-title>Import {{type}}</v-card-title>
        <v-card-text class="pb-0 px-0">
          <v-stepper v-model="step" alt-labels>
            <v-stepper-header>
              <v-stepper-step :step="1">Upload</v-stepper-step><v-divider/>
              <v-stepper-step :step="2">Map data</v-stepper-step><v-divider/>
              <template v-if="defaults">
                <v-stepper-step :step="3">Defaults</v-stepper-step><v-divider/>
              </template>
              <v-stepper-step :step="defaults?4:3">Import</v-stepper-step><v-divider/>
              <v-stepper-step :step="defaults?5:4">Done</v-stepper-step>
            </v-stepper-header>
            <v-stepper-items>

              <v-stepper-content :step="1" class="px-0 pb-0 pt-2">
                <v-card height="calc(100vh - 300px)" class="overflow-y">
                  <v-container fluid>
                    <v-row>
                      <v-col cols="4">
                        <v-file-input outlined hide-details dense v-model="file" accept=".csv" placeholder="Click to select CSV file to import"/>
                      </v-col>
                      <v-col cols="2" class="text-caption mt-2"><a :href="'/'+type+'.csv'" download>Download sample file</a></v-col>
                      <template v-if="usesDates">
                        <v-col cols="2">
                          <v-tooltip bottom nudge-top="20" open-delay="1000">
                            <template v-slot:activator="{on}">
                              <v-text-field label="Imported date format" v-model="dateFormat" :rules="[fieldRules.required]" outlined dense v-on="on"/>
                            </template>
                            <div>Year: yy (e.g. 20), yyyy (e.g. 2021)</div>
                            <div>Month: M (e.g. 1), MM (e.g. 01), MMM (e.g. Jan), MMMM (e.g. January)</div>
                            <div>Day: d (e.g. 1), dd (e.g. 01)</div>
                          </v-tooltip>
                        </v-col>
                        <v-col cols="2" class="mt-2">e.g. {{today | formatDate(dateFormat)}}</v-col>
                      </template>
                    </v-row>
                    <v-row>
                      <v-col class="px-12 text-body-2">
                        The file must have a header line with a description of each field,
                        followed by a line for each item to import.
                        Each field is separated by a comma and enclosed in double quotes.
                        The fields required are:
                        <div v-html="instructions" class="mt-4"/>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </v-stepper-content>

              <v-stepper-content :step="2" class="px-0 pb-0 pt-2">
                <v-card flat width="800">
                  <v-simple-table fixed-header height="calc(100vh - 300px)" class="ml-16">
                    <thead>
                      <tr>
                        <th>PŪRTRAK field</th>
                        <th>Your field</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(field,index) of specifiedFields" :key="index">
                        <td width="45%" class="text-body-2">
                          {{field.name}}
                          <div v-if="!field.mandatory" class="text-caption grey--text">optional</div>
                        </td>
                        <td width="45%"><v-autocomplete :items="yourFields" v-model="field.mapTo" hide-details class="text-body-2" dense outlined/></td>
                        <td width="10%">
                          <template v-if="field.mapTo!=='No match'"><v-icon color="success">mdi-check-circle</v-icon></template>
                          <template v-else-if="field.mandatory"><v-icon color="error">mdi-alert-circle</v-icon></template>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>
              </v-stepper-content>

              <v-stepper-content v-if="defaults" :step="3" class="px-0 pb-0 pt-2">
                <component :is="defaults" height="calc(100vh - 300px)" :defaultValues="defaultValues"/>
              </v-stepper-content>

              <v-stepper-content :step="defaults ? 4 : 3" class="px-0 pb-0 pt-2">
                <v-card class="pl-2">
                  Ready to import {{importCount}} of {{newLines.length}} {{type}}
                  <v-simple-table fixed-header height="calc(100vh - 320px)">
                    <thead>
                      <tr>
                        <th><v-simple-checkbox :value="checked" :indeterminate="indeterminate" @click="check()"/></th>
                        <th v-for="(field,index) of fields" :key="index">{{field.name}}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(line,lineIndex) of newLines" :key="lineIndex">
                        <td><v-simple-checkbox v-model="line.import"/></td>
                        <td v-for="(field,fieldIndex) of line.line" :key="fieldIndex">{{field}}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>
              </v-stepper-content>

              <v-stepper-content :step="defaults ? 5 : 4" class="px-0 pb-0 pt-2">
                <v-card>
                  <v-simple-table fixed-header height="calc(100vh - 320px)">
                    <thead>
                      <tr>
                        <th></th>
                        <th v-for="(field,index) of fields" :key="index">{{field.name}}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(line,lineIndex) of newLines" :key="lineIndex"
                          :class="{'grey--text':!line.import,'red--text':line.status,'green--text':!line.status}">
                        <td class="text-center" max-width="20%">
                          <v-icon v-if="line.import && !line.status" class="green--text">mdi-check</v-icon>
                          <template v-else-if="line.import && line.status">{{line.status}}</template>
                        </td>
                        <td v-for="(field,fieldIndex) of line.line" :key="fieldIndex">{{field}}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>
              </v-stepper-content>

            </v-stepper-items>
          </v-stepper>
        </v-card-text>
        <v-divider/>
        <v-card-actions class="pr-6">
          <v-btn v-if="errors" text color="error" small @click="downloadErrors()" :loading="loading">Download lines with errors</v-btn>
          <v-spacer/>
          <v-btn plain v-if="defaults && step!==5 || !defaults && step!==4"
                       color="primary" @click="showImport=false;$emit('input',false)">Cancel</v-btn>
          <v-btn color="primary" @click="back" v-if="step!==1 && (defaults && step!==5) && (defaults && step!==4)">Back</v-btn>
          <v-btn color="primary" @click="next" :disabled="!nextOkay" :loading="loading">
            <template v-if="defaults && step===4 || !defaults && step===3">Import</template>
            <template v-else-if="defaults && step===5 || !defaults && step===4">Done</template>
            <template v-else>Next</template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.v-stepper {
  box-shadow: none !important;
}
</style>

<script>
import readCSV from '@/plugins/readCSV'
import fileSaver from 'file-saver';
import {format,parse} from 'date-fns'

export default {
  name: 'ImportCsv',
	props: {
    value: {type:Boolean,required:true},

    type: {type:String,required:true},
    fields: {type:Array,required:true},
    importAction: {type:String,required:true},
    instructions: {type:String,required:true},
    defaults: {type:Function,required:false}
  },
  data() {
    return {
      showImport: false,
      step: 1,
      file: null,
      lines: [],
      specifiedFields: [],
      yourFields: [],
      newLines: [],
      loading:false,
      errors: false,
      dateFormat: 'yyyy-MM-dd',
      today: format(new Date(), 'yyyy-MM-dd'),
      defaultValues: {}
    }
  },
  computed: {
    nextOkay() {
      if (this.step === 1 && (!this.file || this.dateFormat == ''))
        return false;
      else if (this.step === 2 && this.specifiedFields.some(f => f.mandatory && f.mapTo === 'No match'))
        return false
      return true;
    },
    usesDates() {
      return this.fields.some(f => f.date);
    },
    indeterminate() {
      return this.newLines.some(line => line.import) && this.newLines.some(line => !line.import);
    },
    checked() {
      return this.newLines.every(line => line.import);
    },
    importCount() {
      return this.newLines.reduce((count,line) => count + (line.import ? 1 : 0), 0);
    }
  },
  watch: {
    value() {
      if (this.value) {
        this.step = 1;
        this.showImport = true;
      }
    },
    showImport() {
      this.$store.commit('setDisableSearchHotKey', this.showImport);
    }
  },
  created() {
    this.specifiedFields = [...this.fields];
  },
  methods: {
    check() {
      const off = this.checked;
      this.newLines.forEach(line => line.import = !off);
    },
    doImport() {
      this.step = 1;
      this.showImport = true;
    },
    back() {
      this.step--;
    },
		async next() {
      this.errors = false;
      if (this.step === 1 && this.file) {
        const reader = new FileReader();
/*
        reader.onloadstart = () => {
console.log("START")
        },
        reader.onloadend = () => {
console.log("END")
        },
        reader.onabort = () => {
console.log("ABORT");
        },
        */
        reader.onload = loadEvent => {
          try {
            this.lines = readCSV(loadEvent.target.result);
          } catch (error) {
            this.$store.commit('setError', {message:`Error in CSV file: ${error}`, type:'error'});
            this.loading = false;
            return;
          }
          if (!this.lines.length) {
            this.$store.commit('setError', {message:'CSV file is empty', type:'error'});
            this.loading = false;
            return;
          }
          this.yourFields = this.lines.splice(0, 1)[0];    // Remove first line, which must be list of field names
          // Try to do initial mapping
          this.specifiedFields.forEach(field =>
            field.mapTo = this.yourFields.find(n => n.toLowerCase() === field.name.toLowerCase()) || 'No match'
          );
          this.yourFields.splice(0, 0, 'No match');
          this.step++;
          this.loading = false;
        }
        reader.onError = () => {
          this.loading = false;
          this.$store.commit('setError', {message:'Error reading CSV file', type:'error'});
        }
        this.loading = true;
        reader.readAsText(this.file);
        return;
      }

      if (this.step === 2 && !this.specifiedFields.some(f => f.mandatory && f.mapTo === 'No match')) {
        // Create new list with mappings from your data to PURTRAK fields
        // First, get index mapping represents in your data
        this.specifiedFields.forEach(field =>
          field.n = field.mapTo !== 'No match' ? this.yourFields.findIndex(name => name === field.mapTo)-1 : -1
        );
        // Now create new list
        this.newLines = [];
        this.importLine = new Array(this.lines.length).fill(true);
        this.status = new Array(this.lines.length).fill('');
        const refDate = new Date();
        this.lines.forEach(line => {
          const newLine = [];
          this.specifiedFields.forEach(field => {
            let value = field.n === -1 ? '' : line[field.n];
            if (field.date) {
              if (value && this.dateFormat !== 'yyyy-MM-dd')    // convert date
                value = format(parse(value, this.dateFormat, refDate), 'yyyy-MM-dd');
              else if (value == '')
                value = format(new Date(), 'yyyy-MM-dd');
            }
            newLine.push(value);
          });

          this.newLines.push({import:true,error:'',line:newLine});
        });
        this.step++;
      }

      else if (this.defaults && this.step === 3) {
        Object.keys(this.defaultValues).forEach(fieldName => {
          const index = this.specifiedFields.findIndex(field => field.name === fieldName);
          if (index !== -1) {
            this.newLines.forEach(line => {
              if (line.line[index] == '')
                line.line[index] = this.defaultValues[fieldName];
            })
          }
        });
        this.step++;
      }

      else if (!this.defaults && this.step === 3 || this.defaults && this.step === 4) {
        this.loading = true;
        const results = await this.$apiCall(`mutation ImportCSV($input: [[String]!]!) {
                                                ${this.importAction}(input:$input)
                                              }`,
                                            {input:this.newLines.filter(line => line.import).map(line => line.line)});
        this.loading = false;
        if (!results.error) {
          let index = 0;
          this.newLines.forEach(line => {
            if (line.import) {
              line.status = results.data[this.importAction][index++];
              this.errors = this.errors || line.status !== '';
            }
          })
          this.step++;
        }
      }

      else if (!this.defaults && this.step === 4 || this.defaults && this.step === 5) {
        this.showImport = false;
        this.$emit('input', false);
        this.$emit('refresh', true);
      }

		},
    downloadErrors() {
      this.loading = true;

      // Header
      let csv = '"ERROR"';
      for (const column of this.fields)
        csv += `,"${column.name}"`;
      csv += '\r\n';

      // Imported lines with errors
      for (const line of this.newLines)
        if (line.import && line.status) {
          let lineCSV = `"${line.status.replace(/\"/g,'""')}"`; // Replace " with "" in error message
          for (let i = 0; i< this.fields.length; i++)
            lineCSV += `,"${line.line[i]}"`;
          csv += `${lineCSV}\r\n`;
        }

      // Write CSV file
      const blob = new Blob([csv], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, `ERRORS ${this.file.name}`);
      this.loading = false;
    }
  }
}
</script>